import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { ZplPrinterSelect } from "@/components/ZplPrinterSelect";
import { ZPL_PRINTER_STORAGE_KEYS, useZplPrinter } from "@/lib/zplPrinter";
import { cn } from "@/lib/utils";
import { formSnapshotDirty } from "@/lib/formDirty";
import { LabelTemplatePreviewEditor } from "@/pages/labelTemplates/LabelTemplatePreviewEditor";
import {
  LABEL_TEMPLATE_OWNER_FILTER_CHIPS,
  templateFamilyGroupKey,
  templateFamilyMatchesOwnerFilters,
  templateFamilyOlderRows,
  templateFamilyVisibleRows,
  templateDetailTitle,
  templateGroupTitle,
  templateIsHubHardcodedPrepack,
  templateHardcodedSourceHint,
  templateManagedByLabel,
  templateStorageLabel,
  templateVersionLabel,
  type LabelTemplateManagedBy,
  type LabelTemplateOwnerBucket,
} from "./labelTemplatesListUi";

interface LabelTemplateRow {
  id: number;
  product_family: string;
  name: string;
  label_type: string | null;
  template: string;
  notes: string | null;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string | null;
  readonly: boolean;
  managed_by: LabelTemplateManagedBy;
  label_size: string | null;
  design_dpi: number | null;
  speed_ips?: number;
  darkness_delta?: number;
}

interface TemplateDraft {
  product_family: string;
  name: string;
  label_type: string;
  notes: string;
  template: string;
}

const EMPTY_DRAFT: TemplateDraft = {
  product_family: "",
  name: "",
  label_type: "",
  notes: "",
  template: "^XA\n^PW{{inches 4}}\n^LL{{inches 6}}\n\n^XZ\n",
};

function draftFromRow(row: LabelTemplateRow): TemplateDraft {
  return {
    product_family: row.product_family,
    name: row.name,
    label_type: row.label_type ?? "",
    notes: row.notes ?? "",
    template: row.template,
  };
}

export function LabelTemplatesPage() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();

  const templatesQuery = useQuery({
    queryKey: ["marketing", "label-templates"],
    queryFn: () => apiFetch<{ rows: LabelTemplateRow[] }>("/api/marketing/label-templates"),
  });
  const { printersQuery, selectedPrinter: selectedZplPrinter, setSelectedPrinter: setSelectedZplPrinter } =
    useZplPrinter({ storageKey: ZPL_PRINTER_STORAGE_KEYS.marketingLabelTemplates });

  const rows = useMemo(() => templatesQuery.data?.rows ?? [], [templatesQuery.data]);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [expandedFamilies, setExpandedFamilies] = useState<Record<string, boolean>>({});
  const [ownerFilters, setOwnerFilters] = useState<Set<LabelTemplateOwnerBucket>>(new Set());
  const [testPrintQuantity, setTestPrintQuantity] = useState(1);
  const [testPrintContext, setTestPrintContext] = useState<{
    zpl: string;
    speed_ips: number;
    darkness_delta: number;
  } | null>(null);

  function toggleOwnerFilter(bucket: LabelTemplateOwnerBucket): void {
    setOwnerFilters((current) => {
      const next = new Set(current);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      return next;
    });
  }

  const selectedRow = rows.find((row) => row.id === selectedId) ?? null;
  const activeDraft = draft ?? (selectedRow != null ? draftFromRow(selectedRow) : null);
  const templateDirty =
    !creating &&
    selectedRow != null &&
    activeDraft != null &&
    formSnapshotDirty(activeDraft, draftFromRow(selectedRow));
  const isReadOnlyTemplate = selectedRow?.readonly === true;
  const isHubHardcodedTemplate =
    selectedRow != null && templateIsHubHardcodedPrepack(selectedRow);
  const isLabelServiceTemplate = selectedRow?.managed_by === "label-service";

  function selectRow(row: LabelTemplateRow): void {
    setSelectedId(row.id);
    setCreating(false);
    setDraft(draftFromRow(row));
    if (!row.is_active) {
      setExpandedFamilies((current) => ({ ...current, [templateFamilyGroupKey(row)]: true }));
    }
  }

  function toggleFamilyExpanded(family: string): void {
    setExpandedFamilies((current) => ({ ...current, [family]: !current[family] }));
  }

  function startNewTemplate(): void {
    setSelectedId(null);
    setCreating(true);
    setDraft({ ...EMPTY_DRAFT });
  }

  function updateDraft(patch: Partial<TemplateDraft>): void {
    setDraft((current) => ({ ...(current ?? activeDraft ?? EMPTY_DRAFT), ...patch }));
  }

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["marketing", "label-templates"] });
  };

  function templateBody(input: TemplateDraft) {
    return {
      product_family: input.product_family.trim(),
      name: input.name.trim(),
      label_type: input.label_type.trim() === "" ? null : input.label_type.trim(),
      notes: input.notes.trim() === "" ? null : input.notes.trim(),
      template: input.template,
    };
  }

  const saveMutation = useMutation({
    mutationFn: (input: { id: number; draft: TemplateDraft }) =>
      apiFetch(`/api/marketing/label-templates/${String(input.id)}`, {
        method: "PUT",
        body: templateBody(input.draft),
      }),
    onSuccess: () => {
      invalidate();
      showToast({ tone: "success", message: "Template saved." });
    },
    onError: (error) => showToast({ tone: "error", message: (error as Error).message }),
  });

  const createMutation = useMutation({
    mutationFn: (input: TemplateDraft) =>
      apiFetch<{ id: number }>("/api/marketing/label-templates", {
        method: "POST",
        body: templateBody(input),
      }),
    onSuccess: (data) => {
      invalidate();
      setCreating(false);
      setSelectedId(data.id);
      showToast({ tone: "success", message: "Template created." });
    },
    onError: (error) => showToast({ tone: "error", message: (error as Error).message }),
  });

  const writeHubBoxGitMutation = useMutation({
    mutationFn: (input: {
      product_family: string;
      version: number;
      template: string;
      speed_ips: number;
      darkness_delta: number;
    }) =>
      apiFetch<{
        ok: boolean;
        path: string;
        speed_ips?: number;
        darkness_delta?: number;
        template?: string;
      }>("/api/marketing/label-templates/write-hub-box-layout", {
        method: "POST",
        body: input,
      }),
    onSuccess: (data, variables) => {
      updateDraft({ template: data.template ?? variables.template });
      invalidate();
      showToast({
        tone: "success",
        message: `Wrote shared Git layout ${data.path}. Every SKU using this version (including no-description contact copy) will pick it up.`,
      });
    },
    onError: (error) => showToast({ tone: "error", message: (error as Error).message }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/marketing/label-templates/${String(id)}/activate`, { method: "POST" }),
    onSuccess: () => {
      invalidate();
      showToast({ tone: "success", message: "Template activated." });
    },
    onError: (error) => showToast({ tone: "error", message: (error as Error).message }),
  });

  const testPrintMutation = useMutation({
    mutationFn: (input: {
      zpl: string;
      printer: string;
      speed_ips: number;
      darkness_delta: number;
      quantity: number;
    }) =>
      apiFetch("/api/marketing/label-templates/test-print", {
        method: "POST",
        body: input,
      }),
    onSuccess: (_data, variables) => {
      const qty = Math.min(99, Math.max(1, Math.round(variables.quantity)));
      showToast({
        tone: "success",
        message:
          qty === 1 ? "Test label sent to printer." : `Sent ${String(qty)} test labels to printer.`,
      });
    },
    onError: (error) => showToast({ tone: "error", message: (error as Error).message }),
  });

  const detailTitle =
    creating
      ? "New template"
      : selectedRow != null
        ? templateDetailTitle(selectedRow)
        : "Template";

  const familyGroups = useMemo(() => {
    const byFamily = new Map<string, LabelTemplateRow[]>();
    for (const row of rows) {
      const groupKey = templateFamilyGroupKey(row);
      const list = byFamily.get(groupKey) ?? [];
      list.push(row);
      byFamily.set(groupKey, list);
    }
    return [...byFamily.entries()].sort(([, leftRows], [, rightRows]) =>
      templateGroupTitle(leftRows).localeCompare(templateGroupTitle(rightRows)),
    );
  }, [rows]);

  const filteredFamilyGroups = useMemo(
    () => familyGroups.filter(([, familyRows]) => templateFamilyMatchesOwnerFilters(familyRows, ownerFilters)),
    [familyGroups, ownerFilters],
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <PageTitleWithHelp hoverHint="ZPL label templates stored in public.label_templates, merged with vendored box/carton/inventory templates from sgs-label-service, hub-owned hub-box labels (FI-3C/5A, FI-5A-W), and pre-pack carton builders. Label-service templates are read-only here. Hub-box and hub hardcoded templates can be edited on this page.">
          Label templates
        </PageTitleWithHelp>
        <div className="flex flex-wrap items-end gap-2">
          {activeDraft != null ? (
            <>
            <ZplPrinterSelect
              id="label-templates-zpl-printer"
              label="Label printer"
              size="compact"
              printersQuery={printersQuery}
              value={selectedZplPrinter}
              onChange={setSelectedZplPrinter}
            />
            <div>
              <Label htmlFor="label-templates-test-qty">Qty</Label>
              <Input
                id="label-templates-test-qty"
                type="number"
                min={1}
                max={99}
                step={1}
                value={testPrintQuantity}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  setTestPrintQuantity(Number.isFinite(next) ? next : 1);
                }}
                className="mt-0.5 h-8 w-[4.5rem]"
              />
            </div>
            <Button
              type="button"
              size="sm"
              isLoading={testPrintMutation.isPending}
              disabled={
                selectedZplPrinter === "" ||
                testPrintContext == null ||
                testPrintContext.zpl === ""
              }
              onClick={() => {
                if (testPrintContext == null || selectedZplPrinter === "") return;
                const quantity = Math.min(99, Math.max(1, Math.round(testPrintQuantity) || 1));
                testPrintMutation.mutate({
                  zpl: testPrintContext.zpl,
                  printer: selectedZplPrinter,
                  speed_ips: testPrintContext.speed_ips,
                  darkness_delta: testPrintContext.darkness_delta,
                  quantity,
                });
              }}
            >
              Print test label
            </Button>
            </>
          ) : null}
          <Button type="button" size="sm" onClick={startNewTemplate}>
            New template
          </Button>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="self-start">
          <CardHeader className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="shrink-0">{`Templates (${String(filteredFamilyGroups.length)})`}</CardTitle>
            <div className="flex flex-wrap items-center gap-1">
              {LABEL_TEMPLATE_OWNER_FILTER_CHIPS.map((chip) => (
                <button
                  key={chip.bucket}
                  type="button"
                  aria-pressed={ownerFilters.has(chip.bucket)}
                  onClick={() => toggleOwnerFilter(chip.bucket)}
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors",
                    ownerFilters.has(chip.bucket) ? chip.activeClass : chip.inactiveClass,
                  )}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardBody className="max-h-[70vh] space-y-3 overflow-y-auto">
            {templatesQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : templatesQuery.isError ? (
              <p className="text-sm text-rose-700">{(templatesQuery.error as Error).message}</p>
            ) : filteredFamilyGroups.length === 0 ? (
              <p className="text-sm text-slate-500">No templates match the selected filters.</p>
            ) : (
              filteredFamilyGroups.map(([family, familyRows]) => {
                const olderRows = templateFamilyOlderRows(familyRows);
                const hasOlderVersions = olderRows.length > 0;
                const expanded = expandedFamilies[family] === true;
                const visibleRows = templateFamilyVisibleRows(familyRows, expanded);

                return (
                <div key={family}>
                  {hasOlderVersions ? (
                    <button
                      type="button"
                      aria-expanded={expanded}
                      onClick={() => toggleFamilyExpanded(family)}
                      className="flex w-full items-center gap-1 rounded-md py-0.5 text-left text-xs font-semibold tracking-wide text-slate-600 hover:bg-slate-50"
                    >
                      <ChevronRight
                        aria-hidden
                        className={cn(
                          "h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform",
                          expanded ? "rotate-90" : "",
                        )}
                        strokeWidth={2}
                      />
                      <span className="min-w-0 flex-1 truncate">{templateGroupTitle(familyRows)}</span>
                      {!expanded ? (
                        <span className="shrink-0 text-[11px] font-normal text-slate-400">
                          {`${String(olderRows.length)} older`}
                        </span>
                      ) : null}
                    </button>
                  ) : (
                    <p className="px-1 text-xs font-semibold tracking-wide text-slate-600">
                      {templateGroupTitle(familyRows)}
                    </p>
                  )}
                  <ul className="mt-1 space-y-1">
                    {visibleRows.map((row) => (
                      <li key={row.id}>
                        <button
                          type="button"
                          onClick={() => selectRow(row)}
                          className={cn(
                            "flex w-full items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-sm",
                            row.id === selectedId
                              ? "border-brand-500 bg-brand-50 text-slate-900"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                          )}
                        >
                          <span className="min-w-0 font-medium tabular-nums">{templateVersionLabel(row)}</span>
                          <span className="flex shrink-0 items-center gap-1">
                            {row.managed_by === "label-service" ? (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                                Label service
                              </span>
                            ) : null}
                            {row.managed_by === "hub" ? (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                                Hub
                              </span>
                            ) : null}
                            <span
                              className={cn(
                                "rounded-full px-2 py-0.5 text-[11px] font-medium",
                                templateStorageLabel(row) === "Supabase"
                                  ? "bg-sky-100 text-sky-900"
                                  : "bg-slate-50 text-slate-500 ring-1 ring-slate-200",
                              )}
                            >
                              {templateStorageLabel(row)}
                            </span>
                            {row.is_active ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                                Active
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                );
              })
            )}
          </CardBody>
        </Card>

        <div className="min-w-0 space-y-4">
          {activeDraft == null ? (
            <Card>
              <CardBody>
                <p className="text-sm text-slate-500">
                  Select a template on the left, or create a new one to begin.
                </p>
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardBody className="space-y-5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <h2 className="text-lg font-semibold text-slate-900">{detailTitle}</h2>
                  {selectedRow != null && !creating ? (
                    <span className="text-sm font-medium tabular-nums text-slate-500">
                      {templateVersionLabel(selectedRow)}
                    </span>
                  ) : null}
                  {isHubHardcodedTemplate ? (
                    <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900">
                      Hardcoded in {templateHardcodedSourceHint(selectedRow.product_family)}
                    </span>
                  ) : null}
                  {isLabelServiceTemplate ? (
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                      Label service
                    </span>
                  ) : null}
                  {selectedRow != null && !creating ? (
                    <span
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-xs font-medium",
                        templateStorageLabel(selectedRow) === "Supabase"
                          ? "bg-sky-100 text-sky-900"
                          : "bg-slate-50 text-slate-500 ring-1 ring-slate-200",
                      )}
                    >
                      {templateStorageLabel(selectedRow)}
                    </span>
                  ) : null}
                  {selectedRow?.is_active ? (
                    <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                      Active
                    </span>
                  ) : null}
                </div>

                <LabelTemplatePreviewEditor
                  apiBasePath="/api/marketing/label-templates"
                  activeDraft={activeDraft}
                  selectedRow={
                    selectedRow == null
                      ? null
                      : {
                          ...selectedRow,
                          useHardcodedLayoutVersion: templateIsHubHardcodedPrepack(selectedRow),
                        }
                  }
                  creating={creating}
                  selectedId={selectedId}
                  selectedZplPrinter={selectedZplPrinter}
                  onTestPrintContextChange={setTestPrintContext}
                  onTemplateChange={(template) => updateDraft({ template })}
                  saveGitPending={writeHubBoxGitMutation.isPending}
                  copyPending={createMutation.isPending}
                  onSaveGitTemplate={
                    selectedRow != null && selectedRow.label_type === "hub-box"
                      ? (template, printSettings) => {
                          writeHubBoxGitMutation.mutate({
                            product_family: selectedRow.product_family,
                            version: selectedRow.version,
                            template,
                            speed_ips: printSettings.speed_ips,
                            darkness_delta: printSettings.darkness_delta,
                          });
                        }
                      : undefined
                  }
                  onCopyTemplateToSupabase={
                    selectedRow != null && selectedRow.label_type === "hub-box"
                      ? (template) => createMutation.mutate({ ...activeDraft, template })
                      : undefined
                  }
                  detailsPanel={
                <>
                <div className="space-y-3 border-t border-slate-200 pt-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Metadata</p>
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <Label htmlFor="template-product-family">Product family</Label>
                      <Input
                        id="template-product-family"
                        value={activeDraft.product_family}
                        onChange={(event) => updateDraft({ product_family: event.target.value })}
                        placeholder="e.g. transfer-carton"
                        className="mt-1"
                        readOnly={isReadOnlyTemplate}
                      />
                    </div>
                    <div>
                      <Label htmlFor="template-name">Name</Label>
                      <Input
                        id="template-name"
                        value={activeDraft.name}
                        onChange={(event) => updateDraft({ name: event.target.value })}
                        placeholder="e.g. transfer-carton"
                        className="mt-1"
                        readOnly={isReadOnlyTemplate}
                      />
                    </div>
                    <div>
                      <Label htmlFor="template-label-type">Label type</Label>
                      <Input
                        id="template-label-type"
                        value={activeDraft.label_type}
                        onChange={(event) => updateDraft({ label_type: event.target.value })}
                        placeholder="e.g. transfer-carton"
                        className="mt-1"
                        readOnly={isReadOnlyTemplate}
                      />
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-4">
                    <div>
                      <Label htmlFor="template-version">Version</Label>
                      <Input
                        id="template-version"
                        value={selectedRow != null ? String(selectedRow.version) : "—"}
                        className="mt-1"
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="template-label-size">Label size</Label>
                      <Input
                        id="template-label-size"
                        value={selectedRow?.label_size?.trim() ? selectedRow.label_size : "—"}
                        className="mt-1"
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="template-id">Template id</Label>
                      <Input
                        id="template-id"
                        value={selectedRow != null ? String(selectedRow.id) : "—"}
                        className="mt-1"
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="template-managed-by">Managed by</Label>
                      <Input
                        id="template-managed-by"
                        value={
                          selectedRow != null
                            ? templateManagedByLabel(selectedRow.managed_by)
                            : "—"
                        }
                        className="mt-1"
                        readOnly
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="template-notes">Notes</Label>
                    <Input
                      id="template-notes"
                      value={activeDraft.notes}
                      onChange={(event) => updateDraft({ notes: event.target.value })}
                      placeholder="What changed in this version"
                      className="mt-1"
                      readOnly={isReadOnlyTemplate}
                    />
                  </div>
                </div>

                <details className="group border-t border-slate-200 pt-5">
                  <summary className="cursor-pointer list-none text-xs font-semibold uppercase tracking-wide text-slate-500 marker:content-none [&::-webkit-details-marker]:hidden">
                    <span className="inline-flex items-center gap-1">
                      <ChevronRight
                        aria-hidden
                        className="h-3.5 w-3.5 text-slate-400 transition-transform group-open:rotate-90"
                        strokeWidth={2}
                      />
                      Template text
                    </span>
                  </summary>
                  <div className="mt-3">
                    <Label htmlFor="template-zpl" className="sr-only">
                      Template (ZPL with Handlebars placeholders)
                    </Label>
                    <textarea
                      id="template-zpl"
                      value={activeDraft.template}
                      onChange={(event) => updateDraft({ template: event.target.value })}
                      rows={16}
                      spellCheck={false}
                      readOnly={isReadOnlyTemplate}
                      className={cn(
                        "w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
                        isReadOnlyTemplate && "bg-slate-50 text-slate-700",
                      )}
                    />
                  </div>
                </details>
                </>
                  }
                />

                {!isReadOnlyTemplate && (creating || selectedRow?.label_type !== "hub-box") ? (
                  <div className="space-y-3 border-t border-slate-200 pt-5">
                    {creating ? (
                      <Button
                        type="button"
                        size="sm"
                        isLoading={createMutation.isPending}
                        disabled={
                          activeDraft.product_family.trim() === "" ||
                          activeDraft.name.trim() === "" ||
                          activeDraft.template.trim() === ""
                        }
                        onClick={() => createMutation.mutate(activeDraft)}
                      >
                        Create in Supabase
                      </Button>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800">Save</p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {selectedRow != null && selectedRow.id <= 0
                              ? "This version is Git. Copy to Supabase only if you need a database snapshot."
                              : "Update overwrites this database row. Copy inserts a new row."}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {selectedRow != null && selectedRow.id > 0 ? (
                            <HubSaveButton
                              type="button"
                              size="sm"
                              dirty={templateDirty}
                              isLoading={saveMutation.isPending}
                              onClick={() => {
                                saveMutation.mutate({ id: selectedRow.id, draft: activeDraft });
                              }}
                            >
                              Update row
                            </HubSaveButton>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            variant={selectedRow != null && selectedRow.id > 0 ? "secondary" : "primary"}
                            isLoading={createMutation.isPending}
                            onClick={() => createMutation.mutate(activeDraft)}
                          >
                            Copy to Supabase
                          </Button>
                          {selectedRow != null && selectedRow.id > 0 && !selectedRow.is_active ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              isLoading={activateMutation.isPending}
                              onClick={() => activateMutation.mutate(selectedRow.id)}
                            >
                              Activate
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
